import { FunctionalComponent, h } from '@stencil/core'
import { JSXBase } from '@stencil/core/internal'
import { noop } from 'lodash-es'
import { ColorsType } from 'src/tailwind'

import { JSX } from '../../../components'
import { IconName } from '../../icons'
import { classes } from '../../utils/css'

export const MenuItem: FunctionalComponent<
  {
    icon: IconName | FunctionalComponent
    iconClass?: string
    hoverBgColor?: ColorsType
    spacing?: 'sm' | 'lg'
    disabledReason?: string
  } & JSXBase.IntrinsicElements['button'] &
    JSXBase.IntrinsicElements['a']
> = (
  {
    class: className = '',
    icon: Icon,
    iconClass = 'opacity-50',
    hoverBgColor = 'surface-upper',
    spacing = 'sm',
    disabled,
    disabledReason,
    ...attrs
  },
  children
) => {
  const Tag = attrs.href ? 'a' : 'button'
  return (
    <Tag
      class={`
        flex w-full items-start hover:bg-${hoverBgColor} rounded transition duration-200 select-none cursor-pointer
        ${classes(spacing === 'sm', 'pl-2 pr-3 py-1 spacing-x-2', 'p-6px spacing-x-5px')}
        ${className}
        ${classes(disabled, 'opacity-25')}
        ${classes(disabled && !disabledReason, 'pointer-events-none', 'pointer-events-auto')}
      `}
      disabled={disabled}
      {...attrs}
    >
      {typeof Icon === 'string' ? (
        <ui-icon class={iconClass} icon={Icon} style={{ fontSize: '1.2em', marginTop: '.1em' }} />
      ) : (
        <Icon />
      )}
      <span class='text-base font-extrabold leading-none text-left'>{children}</span>
      {disabledReason && <ui-tooltip anchor='right'>{disabledReason}</ui-tooltip>}
    </Tag>
  )
}

export const Menu: FunctionalComponent<
  JSX.UiTooltip & {
    bgColor?: ColorsType
    borderColor?: ColorsType
    spacing?: 'sm' | 'lg'
    ref?: (el) => void
    onMenuEnter?: () => void
    onMenuLeave?: () => void
  }
> = (
  {
    bgColor = 'surface-lower',
    borderColor = 'surface-high',
    spacing = 'sm',
    ref = noop,
    onMenuEnter,
    onMenuLeave,
    ...attrs
  },
  children
) => {
  return (
    <ui-tooltip
      ref={(el) => ref?.(el)}
      onPopperEnter={() => onMenuEnter?.()}
      onPopperLeave={() => onMenuLeave?.()}
      allowClick
      anchor='bottom'
      persist
      bgColor={bgColor}
      borderColor={borderColor}
      {...attrs}
    >
      <menu
        class={`flex overflow-hidden flex-col ${classes(
          spacing === 'sm',
          'py-3px px-0',
          'py-7px px-10px'
        )} m-0 whitespace-nowrap spacing-y-3px`}
        style={{ margin: '0 -7px' }}
      >
        {children}
      </menu>
    </ui-tooltip>
  )
}
